/**
 * ElevenLabs TTS provider — Best-in-class voice quality.
 *
 * ElevenLabs produces voices that are near-indistinguishable from humans.
 * Pricing as of 2026:
 *   - Free:    10k chars/month
 *   - Starter: 30k chars/month for $5
 *   - Creator: 100k chars/month for $22
 * A typical Coach paragraph is ~300-500 chars, so the free tier covers
 * 20-30 paragraphs/month. Starter covers 60-100/month.
 *
 * Model defaults to `eleven_turbo_v2_5` — fast + cheap + still very natural.
 * Upgrade to `eleven_multilingual_v2` for the absolute best quality (slower
 * + costs more credits per char).
 *
 * Security: API key in settings.json, read in main process, never reaches
 * the renderer. Same IPC pattern as the Anthropic + OpenAI keys.
 *
 * Voice IDs: defaults to four popular voices from the ElevenLabs public
 * catalog. User can swap to their own custom-cloned voices in Settings.
 */

// Popular default voices from the ElevenLabs catalog. Each ID is the
// voice's permanent identifier from elevenlabs.io/voice-library.
export const ELEVENLABS_DEFAULT_VOICE_MAP = Object.freeze({
  mentor:  '21m00Tcm4TlvDq8ikWAM',  // Rachel — calm, warm female
  analyst: 'pNInz6obpgDQGcFmaJgB',  // Adam — deep, articulate male
  drill:   'VR6AewLTigWG4xSOukaG',  // Arnold — heavy, commanding male
  friend:  'EXAVITQu4vr4xnSDxMaL',  // Bella — soft, conversational female
});

// Friendly names for the Settings UI dropdown. Maps voice ID → display name.
export const ELEVENLABS_VOICE_NAMES = Object.freeze({
  '21m00Tcm4TlvDq8ikWAM': 'Rachel (calm female)',
  'pNInz6obpgDQGcFmaJgB': 'Adam (deep male)',
  'VR6AewLTigWG4xSOukaG': 'Arnold (heavy male)',
  'EXAVITQu4vr4xnSDxMaL': 'Bella (soft female)',
  'AZnzlk1XvdvUeBnXmlld': 'Domi (strong female)',
  'MF3mGyEYCl7XYWbV9V6O': 'Elli (young female)',
  'TxGEqnHWrfWFTfGW9XjX': 'Josh (deep male)',
  'yoZ06aMxZJJ28mfd3POQ': 'Sam (raspy male)',
});

export const ELEVENLABS_MODELS = [
  'eleven_turbo_v2_5',       // fast + cheap (default)
  'eleven_multilingual_v2',  // best quality
  'eleven_monolingual_v1',   // legacy, English-only
];

// Single audio element — same single-stream contract as the other providers.
let _audio = null;
let _objectUrl = null;

const cleanupAudio = () => {
  if (_audio) {
    try { _audio.pause(); } catch { /* */ }
    _audio.src = '';
    _audio = null;
  }
  if (_objectUrl) {
    try { URL.revokeObjectURL(_objectUrl); } catch { /* */ }
    _objectUrl = null;
  }
};

export const isAvailable = () => {
  return typeof window !== 'undefined' &&
         typeof window.electronAPI?.coachTtsCall === 'function' &&
         typeof window.Audio !== 'undefined';
};

/**
 * Fetch + play ElevenLabs audio for the given text.
 * @param {string} text
 * @param {Object} options
 * @param {string} [options.voice='mentor']  Personality id
 * @param {Object} [options.voiceMap]        Override map { mentor: voiceId, ... }
 * @param {string} [options.model='eleven_turbo_v2_5']
 * @param {Function} [options.onStart]
 * @param {Function} [options.onEnd]
 * @returns {Promise<{ok:true} | {ok:false, error:string}>}
 */
export const speak = async (text, options = {}) => {
  if (!isAvailable()) {
    return { ok: false, error: 'ElevenLabs TTS unavailable (Electron IPC bridge missing)' };
  }
  if (typeof text !== 'string' || text.trim().length === 0) {
    return { ok: false, error: 'Empty text' };
  }
  const personality = options.voice || 'mentor';
  const voiceMap = options.voiceMap || ELEVENLABS_DEFAULT_VOICE_MAP;
  const voiceId = voiceMap[personality] || ELEVENLABS_DEFAULT_VOICE_MAP[personality] || ELEVENLABS_DEFAULT_VOICE_MAP.mentor;
  const model = options.model || 'eleven_turbo_v2_5';

  cleanupAudio();

  try {
    const res = await window.electronAPI.coachTtsCall({
      provider: 'elevenlabs',
      text,
      voice: voiceId,
      model,
    });
    if (!res?.ok) {
      return { ok: false, error: res?.error || 'TTS call failed' };
    }
    const bytes = res.audio instanceof Uint8Array ? res.audio : new Uint8Array(res.audio);
    const blob = new Blob([bytes], { type: res.contentType || 'audio/mpeg' });
    _objectUrl = URL.createObjectURL(blob);
    _audio = new window.Audio(_objectUrl);

    return await new Promise((resolve) => {
      _audio.onplay = () => {
        if (typeof options.onStart === 'function') options.onStart();
      };
      _audio.onended = () => {
        if (typeof options.onEnd === 'function') options.onEnd();
        cleanupAudio();
        resolve({ ok: true });
      };
      _audio.onerror = () => {
        if (typeof options.onEnd === 'function') options.onEnd();
        cleanupAudio();
        resolve({ ok: false, error: 'Audio playback failed' });
      };
      const p = _audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch((e) => {
          if (typeof options.onEnd === 'function') options.onEnd();
          cleanupAudio();
          resolve({ ok: false, error: e?.message || 'play() rejected' });
        });
      }
    });
  } catch (e) {
    cleanupAudio();
    return { ok: false, error: e?.message || String(e) };
  }
};

export const stop = () => {
  cleanupAudio();
};
